import React, { useState } from 'react';
import { MapPinIcon, PhoneIcon, WrenchIcon } from '@heroicons/react/24/outline';
import { SaveGestion } from '../../SaveGestion/SaveGestion';
import { TelefonosNuevos } from '../../TelefonosNuevos';
import { HistorialCobranza } from '../../../historialCobranza';
import { CardVerificacionTelefonica } from '../CardVerificacionTelefonica';
import { InfoDireccion } from '../InfoDireccion';
import { DetalleFactura } from '../../DetalleFactura';
import { ReferenciasResumen } from '../../ReferenciasResumen';
import { HistorialGestiones } from '../HistorialGestiones';
import { TablaAmortizacionWeb } from '../../TablaAmortizacionWeb';
import { EstadoCuentaOperacion } from '../../EstadoCuentaOperacion';

/**
 * Componente que gestiona el renderizado del contenido de las distintas pestañas
 * en el Detalle de Cobranza.
 * 
 * @param {Object} props
 * @param {string} props.activeTab - Pestaña actualmente seleccionada
 * @param {function} props.setActiveTab - Función para cambiar la pestaña activa
 * @param {string|number} props.selectedId - ID del registro seleccionado
 * @param {Object} props.data - Datos del crédito
 * @param {Array} props.estadoGestion - Lista de estados de gestión
 * @param {Array} props.historialAPI - Historial de gestiones obtenidas de la API
 * @param {Object} props.direccionData - Datos de la dirección del cliente
 * @param {boolean} props.loading - Estado de carga
 * @param {function} props.handleGuardarTelefonos - Función para guardar teléfonos
 * @param {function} props.refetch - Función para recargar los datos
 */
export const TabContent = ({ 
    activeTab, 
    setActiveTab, 
    selectedId,
    data, 
    estadoGestion, 
    historialAPI, 
    direccionData, 
    loading, 
    handleGuardarTelefonos,
    refetch
}) => {
    // Estado para acordeones de la pestaña VERIFICACION
    const [showDireccion, setShowDireccion] = useState(false);
    const [showVerificacion, setShowVerificacion] = useState(false);

    return (
        <div className="w-full">
            {activeTab === 'GESTION' && (
                <HistorialGestiones historialAPI={historialAPI} estadoGestion={estadoGestion} data={data} />
            )}

            {activeTab === 'VERIFICACION' && (
                <div className="flex flex-col gap-6 w-full animate-fadeIn">
                    {/* Acordeón: Información de Dirección */}
                    <div className="w-full bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                        <button
                            onClick={() => setShowDireccion((prev) => !prev)}
                            className="flex items-center gap-3 px-5 py-4 bg-slate-50 hover:bg-blue-50 text-slate-800 font-bold transition-all duration-200 w-full group border-b border-slate-100"
                        >
                            <div className="p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                                <MapPinIcon className="w-5 h-5 text-blue-600" />
                            </div>
                            Información de Dirección
                            <span className={`ml-auto transition-transform duration-300 text-slate-400 group-hover:text-blue-500 ${showDireccion ? 'rotate-90' : ''}`}>▶</span>
                        </button>
                        {showDireccion && (
                            <div className="p-4 animate-fadeIn">
                                <InfoDireccion direccionData={direccionData} loading={loading} data={data} />
                            </div>
                        )}
                    </div>
                    {/* Acordeón: Verificación Telefónica */}
                    <div className="w-full bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                        <button
                            onClick={() => setShowVerificacion((prev) => !prev)}
                            className="flex items-center gap-3 px-5 py-4 bg-slate-50 hover:bg-blue-50 text-slate-800 font-bold transition-all duration-200 w-full group border-b border-slate-100"
                        >
                            <div className="p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                                <PhoneIcon className="w-5 h-5 text-blue-600" />
                            </div>
                            Verificación Telefónica
                            <span className={`ml-auto transition-transform duration-300 text-slate-400 group-hover:text-blue-500 ${showVerificacion ? 'rotate-90' : ''}`}>▶</span>
                        </button>
                        {showVerificacion && (
                            <div className="p-4 animate-fadeIn">
                                <CardVerificacionTelefonica sCre_SolicitudWeb={data?.sCre_SolicitudWeb} estado={11} estadoGestion={estadoGestion} data={data} />
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className={`animate-fadeIn -mt-4 md:-mt-4 -mx-4 md:-mx-6 ${activeTab === 'SAVE_GESTION' ? 'block' : 'hidden'}`}>
                <SaveGestion
                    isOpen={true}
                    isInline={true}
                    onClose={() => setActiveTab('GESTION')}
                    estadoGestion={estadoGestion}
                    datosCobranza={direccionData}
                    data={data}
                    onSuccess={() => {
                        if (refetch) refetch();
                        setActiveTab('GESTION');
                    }}
                />
            </div>

            {activeTab === 'SIMPLIA' && (
                <div className="animate-fadeIn -mt-4 md:-mt-4 -mx-4 md:-mx-6">
                    <HistorialCobranza
                        idCompra={selectedId}
                        isInline={true}
                        onClose={() => setActiveTab('GESTION')}
                    />
                </div>
            )}

            {activeTab === 'TELEFONOS' && (
                <div className="animate-fadeIn -mt-4 md:-mt-4 -mx-4 md:-mx-6">
                    <TelefonosNuevos
                        isOpen={true}
                        isInline={true}
                        onClose={() => setActiveTab('GESTION')}
                        item={direccionData}
                        data={data}
                        onSave={handleGuardarTelefonos}
                    />
                </div>
            )}

            {activeTab === 'TABLA DE AMORTIZACION' && (
                <div className="animate-fadeIn -mt-4 md:-mt-4 -mx-4 md:-mx-6">
                    <TablaAmortizacionWeb
                        idCompra={data?.idCompra}
                        cliente={data?.Cliente}
                        cedula={data?.Cedula}
                        numeroDocumento={data?.Numero_Documento || data?.Factura_No}
                        isInline={true}
                        onClose={() => setActiveTab('GESTION')}
                    />
                </div>
            )}

            {activeTab === 'PAGOS' && (
                <div className="animate-fadeIn -mt-4 md:-mt-4 -mx-4 md:-mx-6">
                    <EstadoCuentaOperacion
                        idCompra={data?.idCompra}
                        isInline={true}
                    />
                </div>
            )}

            {activeTab === 'FACTURA' && (
                <div className="animate-fadeIn -mt-4 md:-mt-4 -mx-4 md:-mx-6">
                    <DetalleFactura idCompra={data?.idCompra} isInline={true} />
                </div>
            )}

            {activeTab === 'REFERENCIAS' && (
                <div className="animate-fadeIn -mt-4 md:-mt-4 -mx-4 md:-mx-6">
                    <ReferenciasResumen idCompra={data?.idCompra} sCre_SolicitudWeb={data?.sCre_SolicitudWeb} cedula={data?.Cedula} />
                </div>
            )}

            {![ 'GESTION', 'VERIFICACION', 'SAVE_GESTION', 'SIMPLIA', 'TABLA DE AMORTIZACION', 'PAGOS', 'FACTURA', 'REFERENCIAS', 'TELEFONOS' ].includes(activeTab) && (
                <div className="p-12 text-center border-2 border-dashed border-slate-300 rounded-2xl bg-white text-slate-500 font-bold uppercase tracking-widest flex flex-col items-center justify-center min-h-[300px] animate-fadeIn">
                    <WrenchIcon className="w-16 h-16 mb-4 text-slate-300" />
                    Módulo {activeTab} en construcción
                </div>
            )}
        </div>
    );
};
